import {Injectable} from '@angular/core';
import {AngularFirestore} from '@angular/fire/firestore';
import {SessionService} from './session.service';
import {CommonService} from './common.service';
import {MessageService} from './message.service';
import firebase from 'firebase';
import {
  COLLECTION_NAME,
  CURRENT_TEMPERATURE, GENZAI_WASUU,
  HEISHI_TOUTA_PROPERTIES,
  HEISHI_TOUTA_TEISEI_PROPERTIES,
  HEISHI_WASUU,
  HIGH_TEMPERATURE,
  IKUSEI,
  IKUSUU,
  LOW_TEMPERATURE,
  QUERY_LIMIT_SHIYOULOT,
  SHIYOULOT_KEY_SUFFIX,
  SHIYOULOT_VERSION,
  TEISEI_WASUU,
} from '../../shared/constants';
import {
  findGenzaiWasuuError,
  findInvalidWasuuKey,
  WASUU_INTEGER_ERROR_MESSAGE
} from '../../shared/wasuu-validation';
import {MasterService} from './master.service';
import {firstValueFrom, Observable} from 'rxjs';
import {EventService} from './event.service';
import {MatDialog} from '@angular/material/dialog';
import {AngularFireFunctions} from '@angular/fire/functions';
import {Shiyoulot, ShiyoulotV2} from '../../shared/interfaces/shiyoulot';
import {
  KeishaHibetsuFile,
  KyuujiRyouForGraph,
  KyuusuiRyouForGraph,
  TemperatureForGraph
} from '../../shared/interfaces/keisha-hibetsu-file';
import {KeishaHibetsuFileService} from './keisha-hibetsu-file.service';
import {
  ShiyoulotHibetsuFile,
  ShiyoulotHibetsuFileHeishiToutaTeiseiWasuu
} from '../../shared/interfaces/shiyoulot-hibetsu-file';
import {
  GenzaiWasuu,
  HeishiToutaTeiseiWasuu,
  HeishiWasuu,
  KeyString,
  ToutaWasuuDetail,
  yyyyMMdd
} from '../../shared/typeAliases';
import {
  NyuukaHimodukiInfo,
  SelectShiyoulotDialogComponent,
  SelectShiyoulotDialogData,
  SelectShiyoulotDialogResp
} from '../../shared/dialog/select-shiyoulot-dialog/select-shiyoulot-dialog.component';
import {SearchKeyForShiyoulot2} from '../../shared/interfaces/search-key-for-shiyoulot';
import {
  ShiyoulotHibetsuFileWithSeizonRitsu
} from '../../chumon/kyakusaki-teishutsu-chouhyou-seisei/kyakusaki-teishutsu-chouhyou-seisei.component';
import firestore = firebase.firestore;
import {map, take} from 'rxjs/operators';
import { CsvService } from './csv.service';
import {GenzaiWasuuMatomeRowData} from '../../genzai-wasuu/genzai-wasuu.component';
import {ShiyoulotHibetsuFileService} from './shiyoulot-hibetsu-file.service';
import {CsvUtilityService} from './csv-utility.service';

// functions: hibetsuFile-hibetsuFileFunctions-updateHeishiToutaWasuu のリクエストの型
interface UpdateHeishiToutaWasuuRequest extends HeishiWasuu, ToutaWasuuDetail, KeyString<any> {
  shiyoulotNumber: string,
  keishaId: string,
  targetDate: string,
}

/**
 * 現在羽数一覧表示時の行データの型
 * csv出力で使用
 */
interface GenzaiWasuuIchiranRowData {
  shiyoulotNumber: string;
  keishaName: string;
  edukeDate: string;
  keishuRyakushou: string;
  shukkaSakiName: string;
  nichirei: string;
  genzaiWasuu: string;
  dounyuuWasuu: string;
  shukkaWasuu: string;
}

/**
 * 飼養ロットの羽数差分を表す型
 */
interface WasuuSabunForShiyoulotHibetsuFile extends ToutaWasuuDetail, GenzaiWasuu, HeishiWasuu {}

@Injectable({
  providedIn: 'root'
})
export class ShiyoulotService {
  shiyoulotObservable?:Observable<ShiyoulotV2[]>;
  selectedShiyoulot?: ShiyoulotV2;
  constructor(private db: AngularFirestore,
              private afn: AngularFireFunctions,
              private sessionService: SessionService,
              private commonService: CommonService,
              private messageService: MessageService,
              private masterService: MasterService,
              private keishaHibetsuFileService: KeishaHibetsuFileService,
              private shiyoulotHibetsuFileService: ShiyoulotHibetsuFileService,
              private csvUtilityService: CsvUtilityService,
              public eventService: EventService,
              public csvService: CsvService,
              public dialog: MatDialog,) { }

  /**
   *
   * @param collectionName
   */
  genCustomerRootCollectionRef(collectionName: string) : firebase.firestore.CollectionReference<firebase.firestore.DocumentData> {
    const customerId = this.sessionService.session.personalSetting.customerId;
    return this.db.firestore.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId).collection(collectionName);
  }

  /**
   * 飼養ロット初期値セット
   */
  getInitialShiyoulot(): ShiyoulotV2 {
    return {
      version: SHIYOULOT_VERSION,
      kind: COLLECTION_NAME.SHIYOULOT,
      himodukiLotInfo: [],
      genzaiWasuu: 0,
      heishiWasuu: 0,
      toutaWasuu: 0,
      teiseiWasuu: 0,
      nyuukaWasuu: 0,
      shukkaWasuu: 0,
      allNyuukaWasuu: 0,
      allShukkaWasuu: 0,
      koushinKaisuu: 0,
      vaccineProgramId: this.getDefaultVaccineProgramMasterId(),
      isMiNyuuka: true,
      edukeDate: '',
      dounyuuDate: '',
      shukkaDate: ''
    };
  }

  /**
   * ワクチンプログラムマスタでデフォルト設定が true のプログラムid を返す
   * デフォルト設定が true のワクチンプログラムがない場合、有効なワクチンプログラムマスタのidを返す
   * 有効なワクチンプログラムマスタがない場合、undefined を返す
   */
  getDefaultVaccineProgramMasterId() {
    const defaultVaccineProgramMaster = this.masterService.vaccineProgramMaster.find(vaccineProgramMaster => !vaccineProgramMaster.isDeleted && vaccineProgramMaster.isDefaultSetting);
    if (defaultVaccineProgramMaster) {
      return defaultVaccineProgramMaster.id;
    }
    const validVaccineProgramMaster = this.masterService.vaccineProgramMaster.find(vaccineProgramMaster => !vaccineProgramMaster.isDeleted);
    return validVaccineProgramMaster ? validVaccineProgramMaster.id : undefined;
  }

  /**
   *
   * @param id
   */
  getShiyoulot(id: string) {
    const customerId = this.sessionService.session.personalSetting.customerId;
    return firstValueFrom(this.db.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId)
      .collection(COLLECTION_NAME.SHIYOULOT).doc(id).get());
  }

  /**
   * 飼養ロットObservable取得処理(第二期開発)
   * @param kensakuKey
   */
  getShiyoulotObservable2(kensakuKey: SearchKeyForShiyoulot2) {
    const customerId = this.sessionService.session.personalSetting.customerId;
    return this.db.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId)
      .collection(COLLECTION_NAME.SHIYOULOT, ref => {
        let query:
          | firestore.CollectionReference
          | firestore.Query = ref;
        if (kensakuKey?.edukeDate) {
          query = query.where('edukeDate', '==', kensakuKey?.edukeDate);
        }
        if (kensakuKey.edukeDateFrom) {
          query = query.where('edukeDate', '>=', kensakuKey.edukeDateFrom);
        }
        if (kensakuKey.edukeDateTo) {
          query = query.where('edukeDate', '<=', kensakuKey.edukeDateTo);
        }
        if (kensakuKey?.keishuId) {
          query = query.where('keishuId', '==', kensakuKey.keishuId);
        }
        if (kensakuKey?.keishaId) {
          query = query.where('keishaId', '==', kensakuKey.keishaId);
        }
        if (kensakuKey?.isMiNyuuka) {
          query = query.where('isMiNyuuka', '==', kensakuKey.isMiNyuuka);
        }
        if (kensakuKey?.isExist) {
          query = query.where('isExist', '==', kensakuKey.isExist);
        }
        query.orderBy('edukeDate');
        query = query.limit(QUERY_LIMIT_SHIYOULOT);
        return query;
      }).valueChanges({idField: 'id'}) as Observable<ShiyoulotV2[]>;
  }

  /**
   *
   * @param shiyoulotNumber
   */
  getShiyoulotHibetsuFileObservable(shiyoulotNumber: string) {
    const customerId = this.sessionService.session.personalSetting.customerId;
    return this.db.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId)
      .collection(COLLECTION_NAME.SHIYOULOT).doc(shiyoulotNumber)
      .collection(COLLECTION_NAME.HIBETSUFILE)
      .valueChanges({idField: 'id'}) as Observable<ShiyoulotHibetsuFile[]>;
  }

  /**
   * 斃死淘汰羽数等を加減算済みの「現在羽数」及び「生存率」をセットした飼養ロット日別ファイルを返す
   * @param shiyoulotNumber
   * @param date
   * @param shiyoulot
   */
  getShiyoulotHibetsuFileWithSeizonRitsu(shiyoulotNumber: string, date: string, shiyoulot: Shiyoulot | ShiyoulotV2) {
    const customerId = this.sessionService.session.personalSetting.customerId;
    return new Promise<ShiyoulotHibetsuFile & {seizonRitsu?: number} | null>((resolve) => {
      firstValueFrom(this.db.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId)
        .collection(COLLECTION_NAME.SHIYOULOT).doc(shiyoulotNumber)
        .collection(COLLECTION_NAME.HIBETSUFILE).doc(date).get()).then(doc => {
          if (doc.exists) {
            const shiyoulotHibetsuFile: ShiyoulotHibetsuFileWithSeizonRitsu = this.commonService.updateShiyoulotHibetsuFileV1ToV2(doc.data());
            if (!shiyoulotHibetsuFile) {
              console.log('shiyoulotNumber:', shiyoulotNumber, ', date:', date, ', shiyoulot:', shiyoulot);
              return resolve(null);
            }
            shiyoulotHibetsuFile.id = doc.id;
            const lotGenzaiWasuu
             = this.commonService.calcGenzaiWasuuForShiyoulotHibetsuFile(shiyoulotHibetsuFile);
            shiyoulotHibetsuFile.seizonRitsu = lotGenzaiWasuu / (shiyoulot.allNyuukaWasuu ? shiyoulot.allNyuukaWasuu : lotGenzaiWasuu);
            return resolve(shiyoulotHibetsuFile);
          }
          console.log('shiyoulotNumber:', shiyoulotNumber, ', date:', date, ', shiyoulot:', shiyoulot);
          return resolve(null);
        });
    });
  }

  /**
   *
   * @param shiyoulotNumber
   * @param fromDate
   * @param toDate
   */
  getShiyoulotHibetsuFile(shiyoulotNumber: string, fromDate: string, toDate: string) {
    const customerId = this.sessionService.session.personalSetting.customerId;
    return firstValueFrom(this.db.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId)
      .collection(COLLECTION_NAME.SHIYOULOT).doc(shiyoulotNumber)
      .collection(COLLECTION_NAME.HIBETSUFILE, ref => {
        let query:
          | firestore.CollectionReference
          | firestore.Query = ref;
        query = query.where('id', '>=', fromDate);
        query = query.where('id', '<=', toDate);
        return query;
      }).get());
  }

  /**
   * 当日の羽数更新処理。
   * 当日分は冪等に更新されるのでtransactionをはる必要がない
   * @param shiyoulotHibetsuFile
   */
  updateTodayShiyoulotHibetsuFile(shiyoulotHibetsuFile: ShiyoulotHibetsuFile) {
    // 現在羽数がマイナスを許容する
    const newGenzaiWasuu = this.commonService.calcGenzaiWasuuForShiyoulotHibetsuFile(shiyoulotHibetsuFile, false);
    const shiyoulotHibetsuFileUpdateObj = this.genUpdateShiyoulotHibetsuFileObj(shiyoulotHibetsuFile);
    if (this.IsInvalidRequestValue(shiyoulotHibetsuFileUpdateObj)) {
      this.messageService.pushError(WASUU_INTEGER_ERROR_MESSAGE);
      return Promise.reject(WASUU_INTEGER_ERROR_MESSAGE);
    }
    const genzaiWasuuError = findGenzaiWasuuError(
      [shiyoulotHibetsuFile.genzaiWasuu],
      [newGenzaiWasuu]
    );
    if (genzaiWasuuError) {
      this.messageService.pushError(genzaiWasuuError);
      return Promise.reject(genzaiWasuuError);
    }
    const heishiToutaTeiseiObj = this.genHeishiToutaTeiseiObj(shiyoulotHibetsuFileUpdateObj);
    const batch = this.db.firestore.batch();
    const shiyoulotRef = this.genCustomerRootCollectionRef(COLLECTION_NAME.SHIYOULOT)
      .doc(shiyoulotHibetsuFile.shiyoulotId);
    // 飼養ロットを更新
    batch.update(shiyoulotRef, {...heishiToutaTeiseiObj, isExist: newGenzaiWasuu > 0, koushinKaisuu: firebase.firestore.FieldValue.increment(1)});
    // 飼養ロット日別ファイルを更新
    batch.update(shiyoulotRef.collection(COLLECTION_NAME.HIBETSUFILE).doc(shiyoulotHibetsuFile.id),
      shiyoulotHibetsuFileUpdateObj);
    return this.showUpdateCompleted(batch.commit(), '羽数を更新しました', '羽数の更新に失敗しました');
  }

  /**
   * 飼養ロット日別ファイルから斃死淘汰関連のプロパティを取り出してobjectにつめる
   * 不正な値(マイナス)も許容してIsInvalidRequestValue()で別途チェック。
   * @param shiyoulotHibetsuFile
   */
  genUpdateShiyoulotHibetsuFileObj(shiyoulotHibetsuFile: ShiyoulotHibetsuFile) {
    const updateShiyoulotHibetsuFileObj: Partial<ShiyoulotHibetsuFileHeishiToutaTeiseiWasuu> = {};
    this.commonService.keysOf(HEISHI_TOUTA_TEISEI_PROPERTIES)
      .forEach(key => updateShiyoulotHibetsuFileObj[key] = shiyoulotHibetsuFile[key] ?? 0)
    console.log('updateShiyoulotHibetsuFileObj: ', updateShiyoulotHibetsuFileObj);
    return updateShiyoulotHibetsuFileObj;
  }

  /**
   *
   * @param value
   */
  genHeishiToutaTeiseiObj(value: any) {
    let heishiWasuu = 0;
    let toutaWasuu = 0;
    let teiseiWasuu = 0;
    this.commonService.keysOf(HEISHI_TOUTA_TEISEI_PROPERTIES).forEach(genin => {
      if (value[genin] && typeof value[genin] === 'number') {
        if (genin === HEISHI_WASUU) {
          heishiWasuu += value[genin];
        } else if (genin === TEISEI_WASUU) {
          teiseiWasuu += value[genin];
        } else {
          toutaWasuu += value[genin];
        }
      }
    });
    return {heishiWasuu, toutaWasuu, teiseiWasuu};
  }

  /**
   * 過去日を含む複数日更新用の妥当性チェック
   *
   * updateShiyoulotHibetsuFiles() は訂正羽数を更新しないため、
   * 検証対象も斃死淘汰の7項目に限定する。
   * 8項目すべてを見ると、レガシーデータに小数の訂正羽数が残っているロットで
   * 斃死淘汰の編集まで保存できなくなる。
   *
   * @param shiyoulotHibetsuFile 検証対象の飼養ロット日別ファイル
   * @returns {boolean} 不正な値が含まれていたら true
   */
  isInvalidShiyoulotHibetsuFile(shiyoulotHibetsuFile: ShiyoulotHibetsuFile) {
    // 現在羽数がマイナスを許容する
    const newGenzaiWasuu = this.commonService.calcGenzaiWasuuForShiyoulotHibetsuFile(shiyoulotHibetsuFile, false);
    const shiyoulotHibetsuFileUpdateObj = this.genUpdateShiyoulotHibetsuFileObj(shiyoulotHibetsuFile);
    return this.IsInvalidRequestValue(
      shiyoulotHibetsuFileUpdateObj,
      this.commonService.keysOf(HEISHI_TOUTA_PROPERTIES)
    ) || newGenzaiWasuu < 0;
  }

  /**
   * 複数の飼養ロット日別ファイルの羽数更新
   * 本関数では訂正羽数の更新には対応していない(斃死淘汰羽数の更新のみ対応)
   *
   * @param selectedShiyoulotHibetsuFile
   */
  async updateShiyoulotHibetsuFiles(selectedShiyoulotHibetsuFile: ShiyoulotHibetsuFile) {
    const updateObj = this.genUpdateShiyoulotHibetsuFileObj(selectedShiyoulotHibetsuFile);
    const newGenzaiWasuu = this.commonService.calcGenzaiWasuuForShiyoulotHibetsuFile(selectedShiyoulotHibetsuFile, false);
    if (this.IsInvalidRequestValue(updateObj, this.commonService.keysOf(HEISHI_TOUTA_PROPERTIES))) {
      this.messageService.pushError(WASUU_INTEGER_ERROR_MESSAGE);
      return Promise.reject(WASUU_INTEGER_ERROR_MESSAGE);
    }
    if (newGenzaiWasuu < 0) {
      const message = '現在羽数がマイナスになる値は登録できません。';
      this.messageService.pushError(message);
      return Promise.reject(message);
    }

    const shiyoulotRef = this.genCustomerRootCollectionRef(COLLECTION_NAME.SHIYOULOT)
      .doc(selectedShiyoulotHibetsuFile.shiyoulotId);
    // 選択された飼養ロット日別Fの日付から今日までの日付のdiff
    const todayString = this.commonService.getDateWithString();
    const willUpdateDates = this.commonService.getDateDiff(selectedShiyoulotHibetsuFile.id, todayString);

    const promise = this.db.firestore.runTransaction(async (tx) => {
      /** 飼養ロット取得 */
      const shiyoulot = (await tx.get(shiyoulotRef)).data() as ShiyoulotV2;

      // 全数出荷済みの場合は処理を中断
      if (!shiyoulot || this.commonService.calcGenzaiWasuuForShiyoulotFile(shiyoulot) < 1) {
        alert('飼養ロットが全数出荷済です。');
        console.log('This lot has already exported.');
        return Promise.resolve();
      }

      /** 飼養ロット日別ファイル取得 */
      // 選択した飼養ロット日別Fから今日までの日付の飼養ロット日別Fを取得
      const shiyoulotHibetsuFileDocPromiseArray = [];
      // 今日日付も含むため、<=
      for (let i = 0; i <= willUpdateDates; i++) {
        const hibetsuDate = this.commonService.genOffsetDateWithStandardDateString(selectedShiyoulotHibetsuFile.id, i);
        const shiyoulotHibetsuFileRef = shiyoulotRef.collection(COLLECTION_NAME.HIBETSUFILE).doc(hibetsuDate);
        const shiyoulotHibetsuFileDocPromise = tx.get(shiyoulotHibetsuFileRef);
        shiyoulotHibetsuFileDocPromiseArray.push(shiyoulotHibetsuFileDocPromise);
      }
      const shiyoulotHibetsuFileDocs = await Promise.all(shiyoulotHibetsuFileDocPromiseArray);
      const shiyoulotHibetsuFiles = shiyoulotHibetsuFileDocs.map(doc => doc.data() as ShiyoulotHibetsuFile);

      // 選択日の飼養ロット日別Fを取得
      const originalSelectedShiyoulotHibetsuFile = shiyoulotHibetsuFiles.find(shiyoulotHibetsuFile => shiyoulotHibetsuFile.id === selectedShiyoulotHibetsuFile.id);
      if (!originalSelectedShiyoulotHibetsuFile) {
        alert('選択された日付の飼養ロット日別ファイルが見つかりません。');
        console.log('This lot has already exported.');
        return Promise.resolve();
      }

      // selectedShiyoulotHibetsuFileとoriginalSelectedShiyoulotHibetsuFileの羽数差分を取得
      const wasuuSabun = this.calcWasuuSabun(originalSelectedShiyoulotHibetsuFile, selectedShiyoulotHibetsuFile);
      const heishiToutaTeiseiObj = this.calcHeishiToutaWasuuSabun(wasuuSabun);
      const updatedLotGenzaiWasuu = shiyoulot.genzaiWasuu - heishiToutaTeiseiObj.genzaiWasuu;
      const updatedHibetsuGenzaiWasuu = shiyoulotHibetsuFiles.map(file =>
        file.id === selectedShiyoulotHibetsuFile.id ? file.genzaiWasuu : file.genzaiWasuu - wasuuSabun.genzaiWasuu);
      const genzaiWasuuError = findGenzaiWasuuError(
        [shiyoulot.genzaiWasuu, ...shiyoulotHibetsuFiles.map(file => file.genzaiWasuu)],
        [updatedLotGenzaiWasuu, ...updatedHibetsuGenzaiWasuu]
      );
      if (genzaiWasuuError) {
        this.messageService.pushError(genzaiWasuuError);
        return Promise.reject(genzaiWasuuError);
      }
      /** 飼養ロット日別ファイル更新 */
      for (const shiyoulotHibetsuFile of shiyoulotHibetsuFiles) {
        const shiyoulotHibetsuFileRef = this.shiyoulotHibetsuFileService.genShiyoulotHibetsuFileRef(shiyoulotHibetsuFile.shiyoulotId, shiyoulotHibetsuFile.id);

        // 選択した飼養ロット日別Fの場合、斃死淘汰関連のプロパティを更新(現在羽数を更新しない)
        // 選択された飼養ロット日別ファイルから後の日別ファイル：現在羽数のみ更新(差分がある場合)
        const hibetsuFileUpdateObj =
          selectedShiyoulotHibetsuFile.id === shiyoulotHibetsuFile.id ? this.genUpdateShiyoulotHibetsuFileIncrementObj(wasuuSabun)
            : wasuuSabun.genzaiWasuu != 0 ? {genzaiWasuu: shiyoulotHibetsuFile.genzaiWasuu - wasuuSabun.genzaiWasuu}
              : null;

        // 現在羽数がマイナスの場合は処理を中断
        if (hibetsuFileUpdateObj && hibetsuFileUpdateObj?.genzaiWasuu < 0) {
          alert('不正な値が含まれています。');
          return Promise.resolve();
        }

        // 更新対象がある場合、更新
        if (hibetsuFileUpdateObj) {
          tx.update(shiyoulotHibetsuFileRef, hibetsuFileUpdateObj);
        }
      }

      /** 飼養ロット更新 */
      const isExist = this.commonService.calcGenzaiWasuuForShiyoulotFile(shiyoulot) - heishiToutaTeiseiObj.genzaiWasuu > 0;
      // 飼養ロットを更新
      tx.set(shiyoulotRef, {genzaiWasuu: updatedLotGenzaiWasuu, isExist, koushinKaisuu: firebase.firestore.FieldValue.increment(1)}, {merge: true});
    })

    return this.showUpdateCompleted(promise, '羽数を更新しました', '羽数の更新に失敗しました');
  }

  /**
   * 飼養ロット日別ファイルの更新オブジェクトを生成
   * key: 斃死淘汰関連のプロパティ
   * value: firestore.FieldValue.increment
   *
   * 選択した飼養ロット日別Fの場合、斃死淘汰関連のプロパティを更新(現在羽数を更新しない)
   * 選択された飼養ロット日別ファイルから後の日別ファイル：現在羽数のみ更新(差分がある場合)
   *
   * @param wasuuSabun
   */
  genUpdateShiyoulotHibetsuFileIncrementObj(wasuuSabun: WasuuSabunForShiyoulotHibetsuFile): {[key: string | keyof typeof wasuuSabun]: firestore.FieldValue} {
    // 更新オブジェクト
    const obj: {[key: string | keyof typeof wasuuSabun]: firestore.FieldValue} = {};
    for (const key of this.commonService.keysOf(wasuuSabun)) {
      // 現在羽数の更新は除く
      if (key === GENZAI_WASUU) {
        continue;
      }

      obj[key] = firestore.FieldValue.increment(wasuuSabun[key]);
    }
    return obj;
  }

  /**
   * 鶏舎日別ファイル・飼養ロットファイル用に斃死羽数・淘汰羽数単位での差分を算出
   *
   * @param wasuuSabun
   */
  calcHeishiToutaWasuuSabun(wasuuSabun: WasuuSabunForShiyoulotHibetsuFile) {
    const heishiToutaWasuuSabun =
      {
        heishiWasuu: wasuuSabun.heishiWasuu,
        toutaWasuu: this.commonService.calcTouta(wasuuSabun),
        genzaiWasuu: 0
      };
    heishiToutaWasuuSabun.genzaiWasuu = heishiToutaWasuuSabun.heishiWasuu + heishiToutaWasuuSabun.toutaWasuu;
    return heishiToutaWasuuSabun;
  }

  /**
   * 選択された飼養ロット日別ファイルの更新前と後の羽数差分オブジェクトを生成
   *
   * @param originalShiyoulotHibetsuFile
   * @param selectedShiyoulotHibetsuFile
   */
  calcWasuuSabun(originalShiyoulotHibetsuFile: ShiyoulotHibetsuFile,
                 selectedShiyoulotHibetsuFile: ShiyoulotHibetsuFile) {
    const wasuuSabun: WasuuSabunForShiyoulotHibetsuFile = this.genWasuuSabunForShiyoulotHibetsuFile();
    // 斃死・淘汰羽数の差分を取得(現在羽数は除く)
    for (const key of this.commonService.keysOf(HEISHI_TOUTA_PROPERTIES)) {
      // 変更後と変更前の差分を取得
      const requestValue = selectedShiyoulotHibetsuFile[key] ? selectedShiyoulotHibetsuFile[key] : 0;
      const currentValue = originalShiyoulotHibetsuFile[key] ? originalShiyoulotHibetsuFile[key] : 0;
      wasuuSabun[key] = requestValue - currentValue;
      // 変更後と変更前の差分を現在羽数に加算
      wasuuSabun.genzaiWasuu += wasuuSabun[key];
    }
    console.log('wasuuSabun: ', JSON.stringify(wasuuSabun));
    return wasuuSabun;
  }

  /**
   * 飼養ロット日別ファイルの羽数差分オブジェクトの初期値を生成
   */
  genWasuuSabunForShiyoulotHibetsuFile(): WasuuSabunForShiyoulotHibetsuFile {
    return {
      genzaiWasuu: 0,
      ashiWasuu: 0,
      kikeiWasuu: 0,
      kuchiWasuu: 0,
      osuWasuu: 0,
      otherWasuu: 0,
      shouWasuu: 0,
      heishiWasuu: 0,
    };
  }

  /**
   *
   * @param promise
   * @param succeedMessage
   * @param errorMessage
   */
  showUpdateCompleted(promise: Promise<any>, succeedMessage: string, errorMessage: string) {
    return promise.then(() => {
      console.log(succeedMessage);
      this.messageService.pushInfo(succeedMessage);
      console.log('document successfully registered!');
    }).catch(e => {
      console.log(e);
      console.log(errorMessage);
      this.messageService.pushError(errorMessage);
    });
  }

  /**
   *
   * @param selectedShiyoulotHibetsuFile
   */
  genWillShiyoulotHibetsuFileUpdateObj(selectedShiyoulotHibetsuFile: ShiyoulotHibetsuFile): UpdateHeishiToutaWasuuRequest {
    const obj: any = {
      shiyoulotNumber: selectedShiyoulotHibetsuFile.shiyoulotId,
      keishaId: selectedShiyoulotHibetsuFile.keishaId,
      targetDate: selectedShiyoulotHibetsuFile.id,
    };
    // 羽数関連のプロパティをセット
    this.commonService.keysOf(HEISHI_TOUTA_PROPERTIES).forEach(key => {
      obj[key] = selectedShiyoulotHibetsuFile[key]
        ? selectedShiyoulotHibetsuFile[key] : 0;
    });
    console.log(obj);
    return obj;
  }

  /**
   * 各種羽数に整数以外もしくは(訂正羽数以外の)負の値が入っていたら不正とみなす
   *
   * 旧実装は負値チェックが typeof 判定の内側に入れ子になっており、
   * 小数・負値・NaN のいずれも検知できていなかった(2026-08 是正)。
   *
   * @param obj 検証対象の羽数オブジェクト
   * @param keys 検証対象のキー。既定は斃死淘汰＋訂正羽数の8項目
   * @returns {boolean} 不正な値が含まれていたら true
   */
  IsInvalidRequestValue(
    obj: UpdateHeishiToutaWasuuRequest | Partial<ShiyoulotHibetsuFileHeishiToutaTeiseiWasuu>,
    keys: readonly string[] = this.commonService.keysOf(HEISHI_TOUTA_TEISEI_PROPERTIES)
  ) {
    // 訂正羽数のみ負値を許容する(減算方向の訂正があるため)
    return findInvalidWasuuKey(obj as Record<string, unknown>, keys, [TEISEI_WASUU]) !== null;
  }

  /**
   * 以下はグラフにバインドするデータの生成処理
   * 現状は飼養ロット明細からのみ呼び出されている(2022/01/07)
   * @param keishaHibetsuFiles
   */
  getTemperatureSuii(keishaHibetsuFiles: KeishaHibetsuFile[] = []): TemperatureForGraph[] {
    console.log('start getTemperatureSuii');
    const temps: TemperatureForGraph[] = [];
    const dayStart = keishaHibetsuFiles[0].id;
    const dayEnd = keishaHibetsuFiles[keishaHibetsuFiles.length -1].id;
    const daysStartToEnd = 1 + this.commonService.getDateDiff(dayStart, dayEnd);

    for (let i = 0; i < keishaHibetsuFiles.length; i++) {
      const amLowTemps: number[] = [];
      const amCurrentTemps: number[] = [];
      const amHighTemps: number[] = [];
      const pmLowTemps: number[] = [];
      const pmCurrentTemps: number[] = [];
      const pmHighTemps: number[] = [];
      const outSideLowTemps: number[] = [];
      const outSideCurrentTemps: number[] = []; // ここに値が入ることは無いが、メソッドを共通化するために配列を用意しておく
      const outSideHighTemps: number[] = [];
      for (const param in keishaHibetsuFiles[i].temperature) {
        const value = keishaHibetsuFiles[i].temperature[param];
        if (typeof value === 'undefined') { continue; }
        // 最高・最低、午前・午後などで値を振り分ける
        this.commonService.sortTemperatureValueToarray(LOW_TEMPERATURE, param, value, amLowTemps, pmLowTemps, outSideLowTemps);
        this.commonService.sortTemperatureValueToarray(CURRENT_TEMPERATURE, param, value, amCurrentTemps, pmCurrentTemps, outSideCurrentTemps);
        this.commonService.sortTemperatureValueToarray(HIGH_TEMPERATURE, param, value, amHighTemps, pmHighTemps, outSideHighTemps);
      }
      temps.push({
        id: keishaHibetsuFiles[i].id,
        lowTemperatureAM: this.commonService.matAverage(amLowTemps),
        lowTemperaturePM: this.commonService.matAverage(pmLowTemps),
        lowTemperatureOutSide: this.commonService.matAverage(outSideLowTemps),
        currentTemperatureAM: this.commonService.matAverage(amCurrentTemps),
        currentTemperaturePM: this.commonService.matAverage(pmCurrentTemps),
        highTemperatureAM: this.commonService.matAverage(amHighTemps),
        highTemperaturePM: this.commonService.matAverage(pmHighTemps),
        highTemperatureOutSide: this.commonService.matAverage(outSideHighTemps)
      });
    }
    for (let i = 0; i < daysStartToEnd; i++) {
      const y = Number(dayStart.split('-')[0]);
      const m = Number(dayStart.split('-')[1]) - 1;
      const d = Number(dayStart.split('-')[2]);
      const nthDay = new Date(y, m, d + i);
      const dateString = this.commonService.getDateWithString(nthDay);
      if (!temps.some(data => data.id === dateString)) {
        temps.push({
          id: dateString,
          lowTemperatureAM: null,
          lowTemperaturePM: null,
          lowTemperatureOutSide: null,
          currentTemperatureAM: null,
          currentTemperaturePM: null,
          highTemperatureAM: null,
          highTemperaturePM: null,
          highTemperatureOutSide: null
        });
      }
    }
    temps.sort((a, b) => a.id > b.id ? 1 : -1);
    console.log(temps);
    return temps;
  }

  /**
   * 最高気温、最低気温の推移を出力
   * この最高、最低はその日の鶏舎日別ファイルの中で一番高い、もしくは一番低い温度を表す
   * @param keishaHibetsuFiles
   * @param shubetsu 最高気温を取得するか、最低気温を取得するか
   */
  getSpecifiedTemperatureSuii(keishaHibetsuFiles: KeishaHibetsuFile[] = [], shubetsu: 'high' | 'low') {
    console.log('start getTemperatureSuii');
    const dayStart = keishaHibetsuFiles[0].id;
    const dayEnd = keishaHibetsuFiles[keishaHibetsuFiles.length -1].id;
    const daysStartToEnd = 1 + this.commonService.getDateDiff(dayStart, dayEnd);
    const temperatures: {id: string, temperature: number | null}[] = [];
    for (let i = 0; i < keishaHibetsuFiles.length; i++) {
      temperatures.push({id: keishaHibetsuFiles[i].id,
        temperature: shubetsu === 'high'
          ? this.keishaHibetsuFileService.getHighestTemperatureValue(keishaHibetsuFiles[i].temperature)
          : this.keishaHibetsuFileService.getLowestTemperatureValue(keishaHibetsuFiles[i].temperature)});
    }
    this.padNullObjForGraph(temperatures, dayStart, daysStartToEnd, 'temperature');
    temperatures.sort((a, b) => a.id > b.id ? 1 : -1);
    console.log(temperatures);

    return temperatures;
  }

  /**
   * 指定した種別の羽数推移を取得
   * @param keishaHibetsuFiles
   * @param shubetsu 現在羽数、斃死羽数、淘汰羽数、訂正羽数
   */
  getWasuuSuiiForKeisha(keishaHibetsuFiles: KeishaHibetsuFile[] = [], shubetsu: typeof GENZAI_WASUU | keyof HeishiToutaTeiseiWasuu = GENZAI_WASUU) {
    console.log('start getWasuuSuii');
    const dayStart = keishaHibetsuFiles[0].id;
    const dayEnd = keishaHibetsuFiles[keishaHibetsuFiles.length -1].id;
    const daysStartToEnd = 1 + this.commonService.getDateDiff(dayStart, dayEnd);
    const wasuus: {id: string, wasuu: number | null}[] = [];
    for (let i = 0; i < keishaHibetsuFiles.length; i++) {
      wasuus.push({id: keishaHibetsuFiles[i].id,
        wasuu: shubetsu === GENZAI_WASUU
          ? (keishaHibetsuFiles[i].genzaiWasuu ?? 0)
          : this.commonService.calcWasuuForKeishaHibetsuFile(keishaHibetsuFiles[i], shubetsu)});
    }
    this.padNullObjForGraph(wasuus, dayStart, daysStartToEnd, 'wasuu');
    wasuus.sort((a, b) => a.id > b.id ? 1 : -1);
    console.log(wasuus);

    return wasuus;
  }

  /**
   *
   * @param shiyoulotHibetsuFiles
   */
  getWasuuSuiiForShiyoulot(shiyoulotHibetsuFiles: ShiyoulotHibetsuFile[] = []) {
    console.log('start getWasuuSuiiForShiyoulot');
    const dayStart = shiyoulotHibetsuFiles[0].id;
    const dayEnd = shiyoulotHibetsuFiles[shiyoulotHibetsuFiles.length -1].id;
    const daysStartToEnd = 1 + this.commonService.getDateDiff(dayStart, dayEnd);
    const wasuus = [];
    for (let i = 0; i < shiyoulotHibetsuFiles.length; i++) {
      wasuus.push({id: shiyoulotHibetsuFiles[i].id,
        wasuu: this.commonService.calcGenzaiWasuuForShiyoulotHibetsuFile(shiyoulotHibetsuFiles[i])});
    }
    this.padNullObjForGraph(wasuus, dayStart, daysStartToEnd, 'wasuu');
    wasuus.sort((a, b) => a.id > b.id ? 1 : -1);
    console.log(wasuus);

    return wasuus;
  }

  /**
   *
   * @param keishaHibetsuFiles
   */
  getKyuujiRyouSuii(keishaHibetsuFiles: KeishaHibetsuFile[] = []): KyuujiRyouForGraph[] {
    const wasuus = this.getWasuuSuiiForKeisha(keishaHibetsuFiles);
    console.log(keishaHibetsuFiles);
    const dayStart = keishaHibetsuFiles[0].id;
    const dayEnd = keishaHibetsuFiles[keishaHibetsuFiles.length -1].id;
    const daysStartToEnd = 1 + this.commonService.getDateDiff(dayStart, dayEnd);

    const hansouRyous: {id: string, hansouRyou: number | null}[] = [];
    for (let i = 0; i < keishaHibetsuFiles.length; i++) {
      const hansouRyou= keishaHibetsuFiles[i]?.hansouRyou ? keishaHibetsuFiles[i].hansouRyou : null;
      hansouRyous.push({
        id: keishaHibetsuFiles[i].id,
        hansouRyou: hansouRyou ?? null,
      });
    }

    this.padNullObjForGraph(hansouRyous, dayStart, daysStartToEnd, 'hansouRyou');

    const kyuujiRyous: KyuujiRyouForGraph[] = [];
    for (let i = 0; i < hansouRyous.length; i++) {
      kyuujiRyous.push({
        id: hansouRyous[i].id,
        // g/羽で取得（hansouRyouの単位はkg）
        // @ts-ignore
        kyuujiRyou: hansouRyous[i]?.hansouRyou && wasuus[i]?.wasuu && wasuus[i]?.wasuu !== 0 ? Math.round(hansouRyous[i].hansouRyou * 1000 / wasuus[i].wasuu * 10) / 10: null,
      });
    }
    // @ts-ignore
    kyuujiRyous.sort((a, b) => a.id > b.id ? 1 : -1);
    console.log(kyuujiRyous);
    return kyuujiRyous;
  }

  /**
   *
   * @param keishaHibetsuFiles
   */
  getKyuusuiRyouSuii(keishaHibetsuFiles: KeishaHibetsuFile[] = []): KyuusuiRyouForGraph[] {
    console.log('start getKyuusuiRyouSuii');
    const wasuus = this.getWasuuSuiiForKeisha(keishaHibetsuFiles);
    const dayStart = keishaHibetsuFiles[0].id;
    const dayEnd = keishaHibetsuFiles[keishaHibetsuFiles.length -1].id;
    const daysStartToEnd = 1 + this.commonService.getDateDiff(dayStart, dayEnd);
    const floors = this.getFloors(keishaHibetsuFiles[0]);

    const meterDiffs: {id: string, meterDiff: number | null}[] = [];
    for (let i = 0; i < keishaHibetsuFiles.length; i++) {
      meterDiffs.push({
        id: keishaHibetsuFiles[i].id,
        meterDiff: this.keishaHibetsuFileService.calcMeterDiff(keishaHibetsuFiles[i], floors),
      });
    }
    console.log('here', meterDiffs);
    this.padNullObjForGraph(meterDiffs, dayStart, daysStartToEnd, 'meterDiff');

    const kyuusuiRyous: KyuusuiRyouForGraph[] = [];
    for (let i = 0; i < meterDiffs.length; i++) {
      kyuusuiRyous.push({
        id: meterDiffs[i].id,
        // 単位をml/羽に変換。(1000 * 1000)の一つ目の1000は m3 → l への変換、二つ目の1000は l → ml への変換
        // @ts-ignore
        kyuusuiRyou: meterDiffs[i]?.meterDiff && wasuus[i]?.wasuu && wasuus[i]?.wasuu !== 0 ? this.commonService.convertM3ToMl(meterDiffs[i].meterDiff, wasuus[i].wasuu) : null,
      });
    }
    // @ts-ignore
    kyuusuiRyous.sort((a, b) => a.id > b.id ? 1 : -1);
    console.log('kyuusuiRyous', kyuusuiRyous);
    return kyuusuiRyous;
  }

  /**
   * 指定した鶏舎日別ファイルに紐づく鶏舎の階数の配列を取得
   * @param keishaHibetsuFile
   */
  getFloors(keishaHibetsuFile: KeishaHibetsuFile) {
    const keisha = this.masterService.keishaMaster.find(keisha => keisha.id === keishaHibetsuFile.keishaId);
    const floor = keisha && keisha.layout.floor ? keisha.layout.floor : 1;
    return this.commonService.genSequentialNumber(floor);
  }

  /**
   * 値が無い日付をnullが入ったオブジェクトで埋める
   * @param array
   * @param dayStart
   * @param daysStartToEnd
   * @param propertyName
   * @private
   */
  private padNullObjForGraph<T extends {id: yyyyMMdd}>(array: T[], dayStart: string, daysStartToEnd: number, propertyName: keyof T) {
    for (let i = 0; i < daysStartToEnd; i++) {
      const dateString = this.commonService.genOffsetDateWithStandardDateString(dayStart, i);
      if (!array.some(data => data.id === dateString)) {
        array.push({
          id: dateString,
          [propertyName]: null,
        } as T);
      }
    }
    return this.commonService.sortObjectArray(array, 'id');
  }

  /**
   * 2022-02-24に日別ファイルが生成されていなかった不具合の対応用のScript
   * keishaId: 8zp9bQ2bTwF1Ikzqe0Br の「2022-02-22」をコピーして
   * 「2022-02-23」「2022-02-24」を作成
   * 飼養ロット: 11  2  4  6  8の「2022-02-22」をコピーして
   * 「2022-02-23」「2022-02-24」を作成
   * genHibetsuFiles('8zp9bQ2bTwF1Ikzqe0Br', [11,	2, 4,	6,	8], '2022-02-22', '2022-02-23', '2022-02-24')
   * @param nyuukaDate
   * @param genStartDate
   * @param genEndDate
   * @param keishaId
   * @param shiyoulotNumberArray
   */
  async genHibetsuFiles(nyuukaDate: string, genStartDate: string, genEndDate: string, keishaId = '', shiyoulotNumberArray: number[] = []) {
    if (!confirm('書き込みますか?')) {
      return;
    }
    const genHibetsuFileDates = this.commonService.genDateStringInTerm(genStartDate, genEndDate);
    const batch = this.db.firestore.batch();
    if (keishaId != '') {
      // TODO 鶏舎日別Fの現在羽数を飼養ロットFの現在羽数の合計で置き換える必要がある場合、
      // getKeishaHibetsuFileUpdatedGenzaiWasuuを使用する。
      const keishaHibetsuFileDoc = await this.keishaHibetsuFileService.getKeishaHibetsuFile(keishaId, nyuukaDate, nyuukaDate);
      const keishaHibetsuRef = keishaHibetsuFileDoc.docs[0].ref.parent;
      const keishaHibetsuFile = keishaHibetsuFileDoc.docs.map(doc => doc.data())[0];
      for (const date of genHibetsuFileDates) {
        const ref = keishaHibetsuRef.doc(date);
        const tempKeishaHibetsuFile = {...keishaHibetsuFile};
        tempKeishaHibetsuFile.id = date;
        if (tempKeishaHibetsuFile.nyuukaWasuu) {
          tempKeishaHibetsuFile.genzaiWasuu = tempKeishaHibetsuFile.nyuukaWasuu;
          delete tempKeishaHibetsuFile.nyuukaWasuu;
        }
        console.log(date, tempKeishaHibetsuFile);
        batch.set(ref, tempKeishaHibetsuFile);
      }
    }

    if (shiyoulotNumberArray.length) {
      for (const shiyoulotNumber of shiyoulotNumberArray) {
        const shiyoulotHibetsuFileDoc = await this.getShiyoulotHibetsuFile(shiyoulotNumber.toString(), nyuukaDate, nyuukaDate);
        const shiyoulotHibetsuRef = shiyoulotHibetsuFileDoc.docs[0].ref.parent;
        const shiyoulotHibetsuFile = shiyoulotHibetsuFileDoc.docs.map(doc => doc.data())[0];
        const shiyoulotUpdate: any = {genzaiWasuu: shiyoulotHibetsuFile.nyuukaWasuu};
        shiyoulotUpdate.nyuukaWasuu = 0;
        console.log(shiyoulotNumber, shiyoulotUpdate);
        batch.set(shiyoulotHibetsuRef.parent!, shiyoulotUpdate, {merge: true});
        for (const date of genHibetsuFileDates) {
          const shiyoulotRef = shiyoulotHibetsuRef.doc(date);
          const tempShiyoulotHibetsuFile = {...shiyoulotHibetsuFile};
          tempShiyoulotHibetsuFile.id = date;
          if (tempShiyoulotHibetsuFile.nyuukaWasuu) {
            tempShiyoulotHibetsuFile.genzaiWasuu = tempShiyoulotHibetsuFile.nyuukaWasuu;
            delete tempShiyoulotHibetsuFile.nyuukaWasuu;
          }
          console.log(shiyoulotNumber, date, tempShiyoulotHibetsuFile);
          batch.set(shiyoulotRef, tempShiyoulotHibetsuFile);
        }
      }
    }
    if (!confirm('書き込みますか?')) {
      return;
    }
    if (!confirm('書き込みますか?')) {
      return;
    }
    batch.commit().then();
  }

  /**
   * 2022-03-08に対して実行
   * 当日開始時点の各鶏舎の鶏舎日別ファイルの現在羽数を再計算して書き込み
   * WARNING: 引数で当日以外も指定できるようにしてあるが、当日以外の場合に必要な斃死淘汰羽数の
   *  再計算を実装していないので当日以外に対しては実行すべきではない。
   * @param targetDate
   */
  async getKeishaGenzaiWasuu(targetDate: string = this.commonService.getDateWithString()) {
    console.log('getKeishaGenzaiWasuu starts.');
    if (!confirm('本当に更新しますか?')) {
      return;
    }
    const keishaRef = this.genCustomerRootCollectionRef(COLLECTION_NAME.KEISHAMASTER);
    const batch = this.db.firestore.batch();
    const shiyoulotHibetsuPromiseArr: Promise<firebase.firestore.QuerySnapshot<firebase.firestore.DocumentData>>[] = [];
    const keishaHibetsuPromiseArr: Promise<firestore.DocumentSnapshot<firestore.DocumentData>>[] = [];
    const map = new Map();
    // 鶏舎毎に当日の飼養ロット日別ファイルを取得。
    this.masterService.keishaMaster.forEach(keisha => {
      const promise = this.db.firestore.collectionGroup(COLLECTION_NAME.HIBETSUFILE)
        .where('id', '==', targetDate)
        .where('keishaId', '==', keisha.id).get();
      shiyoulotHibetsuPromiseArr.push(promise);
      const keishaPromise = keishaRef.doc(keisha.id).collection(COLLECTION_NAME.KEISHAHIBETSUFILE).doc(targetDate).get();
      keishaHibetsuPromiseArr.push(keishaPromise);
    });
    const [allKeishaSnapShot, keishaHibetsuSnapShot]
      = [await Promise.all(shiyoulotHibetsuPromiseArr), await Promise.all(keishaHibetsuPromiseArr)];
    // 鶏舎毎に飼養ロット日別ファイルの現在羽数を合計。
    allKeishaSnapShot.map(keishaSnapShot => {
      if (keishaSnapShot.empty) { return; }
      let keisha = '';
      const keishaGenzaiWasuu = keishaSnapShot.docs.reduce((pre: any, current) => {
        if (current.exists) {
          const data = current.data();
          keisha = data.keishaId;
          if (data.genzaiWasuu) {
            pre += data.genzaiWasuu;
          }
        }
        return pre;
      }, 0);
      console.log(keisha, keishaGenzaiWasuu);
      map.set(keisha, keishaGenzaiWasuu);
    });
    console.table(map);
    keishaHibetsuSnapShot.forEach(doc => {
      if (!doc.exists) { return; }
      const data = doc.data();
      if (data && data.genzaiWasuu < 0) {
        const defailtGenzaiWasuu = 0;
        console.log(doc.ref.path, data.keishaId, data.genzaiWasuu, '=>', defailtGenzaiWasuu);
        map.set(data.keishaId, 0);
        batch.update(doc.ref, {genzaiWasuu: map.get(data.keishaId)});
      }
    });
    console.table(map);
    // 鶏舎毎にその現在羽数を使って当日の現在羽数を更新。
    for (const key of map.keys()) {
      const ref = keishaRef.doc(key).collection(COLLECTION_NAME.KEISHAHIBETSUFILE).doc(targetDate);
      batch.update(ref, {genzaiWasuu: map.get(key)});
    }
    if (!confirm('本当に更新しますか?')) {
      return;
    }
    if (!confirm('本当に更新しますか?')) {
      return;
    }
    batch.commit();
  }

  /**
   * 飼養ロット選択モーダル開く
   * @param keishuId
   * @param selectedShukkaEventIds 呼び出し元のロット画面で既に紐付けている出荷イベントidの配列
   * @param keishaShubetsu
   * @param edukeDate
   * @param nyuukaHimodukiInfoList
   */
  openSelectShiyoulotDialog(keishuId: string,
                            selectedShukkaEventIds: (string | null | undefined)[] = [],
                            keishaShubetsu?: typeof IKUSUU | typeof IKUSEI,
                            edukeDate?: yyyyMMdd,
                            nyuukaHimodukiInfoList: NyuukaHimodukiInfo[] = []
  ) {
    const dialogRef = this.dialog.open<SelectShiyoulotDialogComponent, SelectShiyoulotDialogData>(SelectShiyoulotDialogComponent, {
      data: {
        keishuId,
        keishaShubetsu,
        edukeDate,
        selectedShukkaEventIds,
        nyuukaHimodukiInfoList
      },
      maxWidth: '90vw',
      minWidth: '0vw',
      height: '90vh',
      panelClass: 'dialog-with-close', // dialog外クリックでdialogを閉じる。
    });

    return firstValueFrom(dialogRef.afterClosed()).then((result: SelectShiyoulotDialogResp) => {
      if (!result) { return; }
      return result;
    });
  }

  /**
   * 現在羽数がずれた場合に訂正するための処理
   * アプリからは呼び出さない、不具合への対応のため手動で実行
   * 2022-08-22実施
   * @param shiyoulotId 修正を行いたい飼養ロットのID
   * @param incidentDate 修正対象期間の始点（問題が発生した日、羽数差異が発生した日の前日）
   */
  async rewriteShiyoulotHibetsuFileGenzaiWasuu(shiyoulotId: string, incidentDate: yyyyMMdd) {
    console.log('rewriteShiyoulotHibetsuFileGenzaiWasuu start;');
    if (!confirm('更新しますか?')) { return; }
    const shiyoulotHibetsuFileDocs
      = await this.getShiyoulotHibetsuFile(shiyoulotId, incidentDate, this.commonService.getDateWithString());
    if (shiyoulotHibetsuFileDocs.empty) { return; }
    const shiyoulotHibetsuFileCollectionRef = shiyoulotHibetsuFileDocs.docs[0].ref.parent;
    const shiyoulotHibetsuFiles = shiyoulotHibetsuFileDocs.docs.map(doc => doc.data()) as ShiyoulotHibetsuFile[];
    const batch = this.db.firestore.batch();
    let sabun = 0; // 当日の現在羽数 - 前日の(現在羽数 - 斃死羽数 - 淘汰羽数)の和
    for (let i = 0; i < shiyoulotHibetsuFiles.length; i++) {
      if (i === 0) { continue; }
      const today = shiyoulotHibetsuFiles[i];
      const yesterday = shiyoulotHibetsuFiles[i-1];
      // 基本的には一致するはず。差異がある場合、その差異を減算する必要がある。
      const tempSabun = today.genzaiWasuu - this.commonService.calcGenzaiWasuuForShiyoulotHibetsuFile(yesterday);
      if (tempSabun) {
        if (!confirm(today.id + ', 現在羽数: ' + today.genzaiWasuu + ', 差分: ' + tempSabun)) { return; }
        console.log(today.id, tempSabun);
        sabun += tempSabun;
      }
      if (sabun) {
        // TODO: FieldValue.incrementではなく現在羽数を冪等に更新できないかは要検討
        //  transactionで処理書ければ気にする必要無い?sabunが0になるはずなので。
        batch.update(shiyoulotHibetsuFileCollectionRef.doc(today.id), {genzaiWasuu: firestore.FieldValue.increment(-sabun)});
      }
    }
    if (!sabun) {
      alert('更新不要');
      return;
    }
    // 飼養ロットの現在羽数も一緒に更新
    if (shiyoulotHibetsuFileCollectionRef.parent && sabun) {
      batch.update(shiyoulotHibetsuFileCollectionRef.parent, {genzaiWasuu: firestore.FieldValue.increment(-sabun)});
    }
    if (!confirm('更新しますか?') || !confirm('更新しますか?')) { return; }
    batch.commit().then(() => console.log('rewriteShiyoulotHibetsuFileGenzaiWasuu end.'));
  }

  /**
   * 鶏舎日別ファイルの現在羽数・斃死淘汰羽数を
   * 飼養ロット日別ファイルの値から再セットする
   * アプリからは呼び出さない、不具合への対応のため手動で実行
   * 2022-08-23、24頃実施
   * @param keishaId
   * @param startDate
   * @param endDate
   */
  async rewriteKeishaHibetsuFileGenzaiWasuu(
    keishaId: string,
    startDate: yyyyMMdd,
    endDate = this.commonService.genOffsetDateWithStandardDateString(this.commonService.getDateWithString(), -1)) {
    console.log('rewriteKeishaHibetsuFileGenzaiWasuu start.');
    // 対象期間における各ロットの日別ファイル取得
    // keishaIdとidをkeyにcollectionGroupで取得?
    const shiyoulotHibetsuPromiseArr = [];
    const batch = this.db.firestore.batch();
    const keishaHibetsuRef = this.genCustomerRootCollectionRef(COLLECTION_NAME.KEISHAMASTER)
      .doc(keishaId).collection(COLLECTION_NAME.KEISHAHIBETSUFILE);
    const dates = this.commonService.genDateStringInTerm(startDate, endDate);
    // 各日付の飼養ロット日別ファイルを取得
    for (const date of dates) {
      const promise = this.db.firestore.collectionGroup(COLLECTION_NAME.HIBETSUFILE)
        .where('id', '==', date)
        .where('keishaId', '==', keishaId).get();
      shiyoulotHibetsuPromiseArr.push(promise);
    }
    // 日付毎の飼養ロット日別ファイルDocが入った配列
    const shiyoulotHibetsuFileDocsByDate = await Promise.all(shiyoulotHibetsuPromiseArr);
    if (!shiyoulotHibetsuFileDocsByDate.length) { return; }
    // 各日付における現在羽数の合計
    const genzaiWasuuByDate = this.commonService.deleteUndefinedOrNullFromArray(shiyoulotHibetsuFileDocsByDate
      .map(docsArray => {
        if (docsArray.empty) { return null; }
        return docsArray.docs.reduce((
          prev: { date: string, genzaiWasuu: number, heishiWasuu: number, toutaWasuu: number },
          current: firebase.firestore.QueryDocumentSnapshot<firebase.firestore.DocumentData>) => {
          if (current.exists) {
            const hibetsuFile = current.data() as ShiyoulotHibetsuFile;
            prev.date = hibetsuFile.id; // 何回上書きしても同じ値が書き込まれるはずなので毎回上書き
            prev.genzaiWasuu += this.commonService.calcGenzaiWasuuForShiyoulotHibetsuFile(hibetsuFile);
            prev.heishiWasuu += hibetsuFile.heishiWasuu ? hibetsuFile.heishiWasuu : 0;
            prev.toutaWasuu += this.commonService.calcTouta(hibetsuFile);
          }
          return prev;
        }, {date: '', genzaiWasuu: 0, heishiWasuu: 0, toutaWasuu: 0});
      }));
    console.table(genzaiWasuuByDate);
    if (!genzaiWasuuByDate.length) { return; }
    const sortedGenzaiWasuuByDate = this.commonService.sortMultiKeys(genzaiWasuuByDate, [{key: 'date', direction: 1}]);
    // 前日の飼養ロット現在羽数の合計で当日の鶏舎日別ファイルの現在羽数を更新
    for (let i = 0; i < sortedGenzaiWasuuByDate.length; i++) {
      if (!sortedGenzaiWasuuByDate[i].date) { continue; }
      const updateDate = this.commonService.genOffsetDateWithStandardDateString(sortedGenzaiWasuuByDate[i].date, 1);
      const updateObj: {genzaiWasuu: number, heishiWasuu?: number, toutaWasuu?: number} = {genzaiWasuu: sortedGenzaiWasuuByDate[i].genzaiWasuu};
      // 鶏舎日別ファイルの現在羽数は前日の値、斃死淘汰羽数は当日の値で書き込み
      // TODO: 最後の日の斃死淘汰羽数を適切に更新する必要有
      if (sortedGenzaiWasuuByDate[i + 1]) {
        updateObj.heishiWasuu = sortedGenzaiWasuuByDate[i + 1].heishiWasuu;
        updateObj.toutaWasuu = sortedGenzaiWasuuByDate[i + 1].toutaWasuu;
      }
      console.log(updateDate, updateObj);
      batch.update(keishaHibetsuRef.doc(updateDate), updateObj);
    }
    // commitする
    if (!confirm('更新しますか?') || !confirm('更新しますか?')) { return; }
    batch.commit().then(() => console.log('rewriteKeishaHibetsuFileGenzaiWasuu end.'));
  }

  /** 飼養ロットファイル更新スクリプト
   * アプリからは呼び出さない、不具合への対応のため手動で実行
   * 2022-08-29実施 (羽数関連で未定義のプロパティは初期値: 0 で定義、version: 2 を追加)
   * 2022-10-12実施 (導入日、出荷日をセット)
   */
  async updateShiyoulotV2() {
    const customerId = this.sessionService.session.personalSetting.customerId;
    const shiyoulotRef = this.db.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId)
      .collection(COLLECTION_NAME.SHIYOULOT);
    const batch = this.db.firestore.batch();
    await firstValueFrom(shiyoulotRef.get()).then(shiyoulots => {
      shiyoulots.docs.forEach(shiyoulot => {
        const shiyoulotMeisai = shiyoulot.data();
        /** 羽数関連で未定義のプロパティは初期値: 0 で定義、version: 2 を追加 **/
        // const updateObj: any = { version: SHIYOULOT_VERSION };
        // updateObj.genzaiWasuu = this.commonService.getNumberOrZero(shiyoulotMeisai.genzaiWasuu);
        // updateObj.heishiWasuu = this.commonService.getNumberOrZero(shiyoulotMeisai.heishiWasuu);
        // updateObj.toutaWasuu = this.commonService.getNumberOrZero(shiyoulotMeisai.toutaWasuu);
        // updateObj.teiseiWasuu = this.commonService.getNumberOrZero(shiyoulotMeisai.teiseiWasuu);
        // updateObj.nyuukaWasuu = this.commonService.getNumberOrZero(shiyoulotMeisai.nyuukaWasuu);
        // updateObj.shukkaWasuu = this.commonService.getNumberOrZero(shiyoulotMeisai.shukkaWasuu);
        // updateObj.allNyuukaWasuu = this.commonService.getNumberOrZero(shiyoulotMeisai.allNyuukaWasuu);
        // updateObj.allShukkaWasuu = this.commonService.getNumberOrZero(shiyoulotMeisai.allShukkaWasuu);
        /** 導入日、出荷日をセット **/
        const updateObj: any = {};
        const keishaShubetsu = this.commonService.getKeishaShubetsu(shiyoulotMeisai.keishaId, this.masterService.keishaMaster);
        if (!shiyoulotMeisai.dounyuuDate) {
          // 育雛かつ導入日が未定義 or nullの場合、導入日に餌付け日をセット
          if (keishaShubetsu === IKUSUU) {
            updateObj.dounyuuDate = shiyoulotMeisai.edukeDate ? shiyoulotMeisai.edukeDate
              : shiyoulotMeisai.yoteiEdukeDate ? shiyoulotMeisai.yoteiEdukeDate
                : '';
          } else if (keishaShubetsu === IKUSEI) {
            // 育成かつ導入日が未定義 or nullの場合、導入日に予定入荷日 or 空文字をセット
            updateObj.dounyuuDate = shiyoulotMeisai.yoteiNyuukaDate ? shiyoulotMeisai.yoteiNyuukaDate : '';
          }
        }
        // 出荷日が未定義 or nullの場合、出荷日は予定出荷日があれば、予定出荷日をセット。
        if (!shiyoulotMeisai.shukkaDate) {
          updateObj.shukkaDate = shiyoulotMeisai.yoteiShukkaDate ? shiyoulotMeisai.yoteiShukkaDate : '';
        }
        if (updateObj.shukkaDate === '' || updateObj.dounyuuDate === '') {
          console.log(shiyoulotMeisai);
          console.log(updateObj);
        }
        batch.update(shiyoulot.ref, updateObj);
      });
    });
    if (confirm('更新しますか？')) {
      batch.commit().then();
    }
  }

  /**
   * 空舎期間中の現在羽数を0羽に更新するためのスクリプト
   * 鶏舎と期間を指定して更新する
   *
   * アプリからは呼び出さない、不具合への対応のため手動で実行
   * 2023-09-07実施
   * @param keishaId
   * @param startDate
   * @param endDate
   */
  async updateKeishaHibetsuFileKuushaKikan(keishaId: string, startDate = '', endDate = '') {
    console.log('updateKeishaHibetsuFileKuushaKikan start.');
    const batch = this.db.firestore.batch();
    const buffer: any[] = [];
    // TODO 鶏舎日別Fの現在羽数を飼養ロットFの現在羽数の合計で置き換える必要がある場合、
    // getKeishaHibetsuFileUpdatedGenzaiWasuuを使用する。
    const keishaHibetsuFiles = await this.keishaHibetsuFileService.getKeishaHibetsuFile(keishaId, startDate, endDate);
    keishaHibetsuFiles.forEach(doc => {
      if (doc.exists) {
        const data = this.commonService.updateKeishaHibetsuFileV1ToV2(doc.data());
        // 鶏舎日別ファイルが更新されていれば、確認
        if ((data.operatorAM || data.operatorPM) && !confirm(`更新されている可能性があります。この日の鶏舎日別ファイルも更新しますか?: ${data.id}`)) {
          return;
        }
        buffer.push({id: data.id,genzaiWasuu: data.genzaiWasuu})
        batch.update(doc.ref, {genzaiWasuu: 0});
      }
    })
    console.table(buffer);
    // commitする
    if (!confirm('更新しますか?') || !confirm('更新しますか?')) { return; }
    batch.commit().then(() => console.log('updateKeishaHibetsuFileKuushaKikan end.'));
  }

  /**
   * ShiyoulotV2のObservableから、まとめ表示用のGenzaiWasuuMatomeCsvのObservableに変換する処理
   *
   * @param shiyoulot$
   */
  genGroupedByAgeAndBreedShiyoulotObservable(shiyoulot$: Observable<ShiyoulotV2[]>) {
    return shiyoulot$
      .pipe(
        map(data => this.commonService.groupBy(data, item => item.keishuId + '@' + this.commonService.getShiyoulotNichirei(item))),
        map(g => {
          const arr: GenzaiWasuuMatomeRowData[] = [];
          for (const [key, val] of Object.entries(g)) {
            const keishaId = val[0].keishaId ?? ''; // 鶏舎毎にまとまっているため全て同じ
            const keishuId = key.split('@')[0];
            const keishuName = this.commonService.getName(this.masterService.keishuMaster, keishuId);
            const keishuRyakushou = this.commonService.getName(this.masterService.keishuMaster, keishuId, 'keishuRyakushou');
            const nichirei = key.split('@')[1];
            const matomeWasuu = val.reduce((a: number, b: ShiyoulotV2) => a + this.commonService.calcGenzaiWasuuForShiyoulotFile(b), 0).toString();
            arr.push({keishaId, keishuName, keishuRyakushou, nichirei, matomeWasuu});
          }
          return arr;
        })
      );
  }

  /**
   * 現在羽数一覧CSVを出力
   *
   * @param shiyoulotList
   * @param farmName
   */
  exportCSVForGenzaiWasuu(shiyoulotList: ShiyoulotV2[], farmName: string): void {
    const genzaiWasuuIchiranArr = shiyoulotList.reduce((prev: GenzaiWasuuIchiranRowData[], currentShiyoulot: ShiyoulotV2) => {
      const rowData = this.getInitialGenzaiWasuuIchiranRowData();
      rowData.shiyoulotNumber = currentShiyoulot.id ?? '';
      rowData.keishaName = this.commonService.getName(this.masterService.keishaMaster, currentShiyoulot.keishaId);
      rowData.edukeDate = this.commonService.getEdukeDate(currentShiyoulot);
      rowData.keishuRyakushou = this.commonService.getName(this.masterService.keishuMaster, currentShiyoulot.keishuId, 'keishuRyakushou');
      rowData.shukkaSakiName = this.commonService.getName(this.masterService.shukkaSakiMaster, currentShiyoulot.daihyouShukkaSakiId);
      rowData.nichirei = this.commonService.getShiyoulotNichirei(currentShiyoulot) as string;
      rowData.genzaiWasuu = this.commonService.calcGenzaiWasuuForShiyoulotFile(currentShiyoulot).toString();
      rowData.dounyuuWasuu = currentShiyoulot.allNyuukaWasuu.toString();
      rowData.shukkaWasuu = currentShiyoulot.allShukkaWasuu.toString();

      prev.push(rowData);
      return prev;
    }, []);

    const fileName = '現在羽数一覧_' + farmName + '_' + this.commonService.getDateWithString()

    this.csvService.exportCsv(
      genzaiWasuuIchiranArr,
      this.genGenzaiWasuuIchiranPropertiesForCSV(),
      'horizontal',
      fileName
    );

  }

  /**
   * 現在羽数一覧まとめCSVを出力
   *
   * @param groupedByAgeAndBreedShiyoulotList
   * @param farmName
   */
  exportCSVForGenzaiWasuuMatome(groupedByAgeAndBreedShiyoulotList: GenzaiWasuuMatomeRowData[], farmName: string): void {
    const genzaiWasuuIchiranMatomeArr = groupedByAgeAndBreedShiyoulotList.reduce((prev: GenzaiWasuuIchiranRowData[], matomeGenzaiWasuuCsv: GenzaiWasuuMatomeRowData) => {
      const rowData = this.getInitialGenzaiWasuuIchiranRowData();
      rowData.keishaName = this.commonService.getName(this.masterService.keishaMaster, matomeGenzaiWasuuCsv.keishaId);
      rowData.keishuRyakushou = matomeGenzaiWasuuCsv.keishuRyakushou;
      rowData.nichirei = matomeGenzaiWasuuCsv.nichirei;
      rowData.genzaiWasuu = matomeGenzaiWasuuCsv.matomeWasuu;

      prev.push(rowData);
      return prev;
    }, []);

    const fileName = '現在羽数一覧まとめ_' + farmName + '_' + this.commonService.getDateWithString()

    this.csvService.exportCsv(
      genzaiWasuuIchiranMatomeArr,
      this.genGenzaiWasuuIchiranPropertiesForCSV(),
      'horizontal',
      fileName
    );

  }

  /**
   * 各飼養ロット毎の現在羽数一覧データの初期値を返す
   */
  getInitialGenzaiWasuuIchiranRowData(): GenzaiWasuuIchiranRowData {
    return {
      keishaName: '',
      shiyoulotNumber: '',
      edukeDate: '',
      keishuRyakushou: '',
      shukkaSakiName: '',
      nichirei: '',
      genzaiWasuu: '',
      dounyuuWasuu: '',
      shukkaWasuu: '',
    };
  }

  /**
   * 現在羽数一覧CSVのヘッダ情報を生成
   * @private
   */
  genGenzaiWasuuIchiranPropertiesForCSV() {
    return [
      {prop: 'keishaName', name: '鶏舎名'},
      {prop: 'shiyoulotNumber', name: '飼養ロット番号'},
      {prop: 'edukeDate', name: '餌付日'},
      {prop: 'keishuRyakushou', name: '鶏種'},
      {prop: 'shukkaSakiName', name: '出荷先'},
      {prop: 'nichirei', name: '日齢'},
      {prop: 'genzaiWasuu', name: '現在羽数'},
      {prop: 'dounyuuWasuu', name: '導入羽数'},
      {prop: 'shukkaWasuu', name: '出荷羽数'},
    ];
  }

  /**
   * observableを元に飼養ロット一覧CSVを出力
   */
  exportShiyoulotIchiranCSV(): void {
    if (!this.shiyoulotObservable) { return; }
    // shiyoulotObservableからshiyoulotの配列を取得
    this.shiyoulotObservable.pipe(take(1)).subscribe(shiyoulotList => {
        this.exportShiyoulotIchiranCSVMain(shiyoulotList);
    });
  }

  /**
   * 飼養ロット一覧CSVを出力
   */
  exportShiyoulotIchiranCSVMain(shiyoulotList: ShiyoulotV2[]): void {
    const csvRows = shiyoulotList.map(shiyoulot => {
      return this.csvUtilityService.genShiyoulotIchiranCSVRow(shiyoulot);
    });
    this.csvService.exportCsv(
      csvRows,
      this.csvUtilityService.genShiyoulotIchiranPropertiesForCSV(),
      'horizontal',
      '飼養ロット一覧_' + this.commonService.getDateWithString()
    );
  }
}
